import React, { useState, useRef, useEffect, forwardRef } from 'react'
import { PhoneInputProps } from './PhoneInput.types'
import {
  PhoneInputWrapper,
  FlagButton,
  CountryDropdown,
  CountryList,
  CountryItem,
  CountryFlag,
  CountryName,
  CountryCode,
  SearchInputWrapper,
  DropdownArrow,
  StyledPhoneInput,
  FlagButtonWrapper,
} from './PhoneInput.styles'
import { Icon } from '../Icon'
import { Input } from '../Input'
import { countries, getDefaultCountry, Country } from './countries'
import {
  detectCountryFromNumber,
  formatPhoneNumber,
  validatePhoneNumber,
  parsePhoneNumber,
  cleanPhoneNumber,
  hasInternationalPrefix,
} from './phoneUtils'

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value = '',
      onChange,
      onValidation,
      defaultCountry = 'CH',
      preferredCountries = [],
      onlyCountries,
      excludeCountries = [],
      searchPlaceholder = 'Search countries...',
      noMatchesText = 'No matching countries',
      enableFormatting = true,
      enableAutoDetection = true,
      size = 'medium',
      error = false,
      fullWidth = false,
      disabled = false,
      placeholder = 'Phone number',
      className,
      ...props
    },
    ref
  ) => {
    const [selectedCountry, setSelectedCountry] = useState<Country>(() => {
      // Try to detect country from initial value if provided
      if (value && hasInternationalPrefix(value)) {
        const detected = detectCountryFromNumber(value)
        if (detected) return detected
      }

      // Otherwise use defaultCountry or browser locale
      if (defaultCountry) {
        const country = countries.find(
          (c) => c.code === defaultCountry.toUpperCase()
        )
        if (country) return country
      }
      return getDefaultCountry()
    })
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [highlightedIndex, setHighlightedIndex] = useState(0)
    const [internalValue, setInternalValue] = useState(value)
    const [cursorPosition, setCursorPosition] = useState<number | null>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const phoneInputRef = useRef<HTMLInputElement>(null)
    const isManualCountryChange = useRef(false)

    // Filter countries based on props
    const filteredCountries = countries.filter((country) => {
      if (onlyCountries && onlyCountries.length > 0) {
        return onlyCountries.includes(country.code)
      }
      if (excludeCountries.length > 0) {
        return !excludeCountries.includes(country.code)
      }
      return true
    })

    // Search through countries
    const searchedCountries = filteredCountries.filter((country) => {
      const query = searchQuery.toLowerCase()
      return (
        country.name.toLowerCase().includes(query) ||
        country.dialCode.includes(query) ||
        country.code.toLowerCase().includes(query)
      )
    })

    // Sort countries with preferred ones first
    const sortedCountries = [...searchedCountries].sort((a, b) => {
      const aIsPreferred = preferredCountries.includes(a.code)
      const bIsPreferred = preferredCountries.includes(b.code)
      if (aIsPreferred && !bIsPreferred) return -1
      if (!aIsPreferred && bIsPreferred) return 1
      return 0
    })

    // Handle dropdown close on outside click
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsDropdownOpen(false)
        }
      }

      if (isDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
          document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isDropdownOpen])

    // Focus search input when dropdown opens
    useEffect(() => {
      if (isDropdownOpen && searchInputRef.current) {
        searchInputRef.current.focus()
      }
    }, [isDropdownOpen])

    // Sync internal value with external value
    useEffect(() => {
      if (value !== internalValue) {
        setInternalValue(value)
      }
    }, [value])

    // Auto-detect country from phone number as user types
    useEffect(() => {
      if (
        !enableAutoDetection ||
        !internalValue ||
        isManualCountryChange.current
      ) {
        isManualCountryChange.current = false
        return
      }

      // Only auto-detect if the input has international prefix
      if (hasInternationalPrefix(internalValue)) {
        const detected = detectCountryFromNumber(internalValue, selectedCountry)
        if (detected && detected.code !== selectedCountry.code) {
          setSelectedCountry(detected)
        }
      }
    }, [internalValue, enableAutoDetection])

    // Validate phone number
    useEffect(() => {
      if (!onValidation) return

      if (!internalValue || internalValue.trim() === '') {
        onValidation(true) // Empty is valid
        return
      }

      const validation = validatePhoneNumber(internalValue, selectedCountry)
      onValidation(validation.isValid, validation.error)
    }, [internalValue, selectedCountry, onValidation])

    const handleCountrySelect = (country: Country) => {
      isManualCountryChange.current = true
      setSelectedCountry(country)
      setIsDropdownOpen(false)
      setSearchQuery('')
      setHighlightedIndex(0)

      // Focus phone input after country selection
      if (phoneInputRef.current) {
        phoneInputRef.current.focus()
      }

      // Preserve the phone number when manually switching countries
      // Parse current value and reformat with new country
      if (internalValue && !hasInternationalPrefix(internalValue)) {
        const parsed = parsePhoneNumber(internalValue, selectedCountry)
        if (parsed.nationalNumber) {
          const newFormatted = formatPhoneNumber(internalValue, country)
          if (onChange) {
            onChange(newFormatted, country)
          }
        }
      } else if (onChange) {
        onChange(internalValue, country)
      }
    }

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      const cursorPos = e.target.selectionStart

      // Store cursor position
      setCursorPosition(cursorPos)

      // Update internal state
      setInternalValue(newValue)

      // Call onChange with the new value
      if (onChange) {
        onChange(newValue, selectedCountry)
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isDropdownOpen) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev < sortedCountries.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
          break
        case 'Enter':
          e.preventDefault()
          if (sortedCountries[highlightedIndex]) {
            handleCountrySelect(sortedCountries[highlightedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsDropdownOpen(false)
          break
      }
    }

    // Get display value (formatted or raw)
    const displayValue = React.useMemo(() => {
      if (!enableFormatting || !value) {
        return value
      }

      // If the input has international prefix, format it
      if (hasInternationalPrefix(value)) {
        const parsed = parsePhoneNumber(value, selectedCountry)
        if (parsed.nationalNumber) {
          return formatPhoneNumber(value, selectedCountry)
        }
      }

      return value
    }, [value, selectedCountry, enableFormatting])

    return (
      <PhoneInputWrapper $fullWidth={fullWidth} className={className}>
        <FlagButtonWrapper>
          <FlagButton
            type="button"
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
            $size={size}
            $error={error}
            aria-label="Select country"
            aria-expanded={isDropdownOpen}
          >
            <CountryFlag>{selectedCountry.flag}</CountryFlag>
            <CountryCode>{selectedCountry.dialCode}</CountryCode>
            <DropdownArrow $isOpen={isDropdownOpen}>
              <Icon name="arrow-down" size="small" />
            </DropdownArrow>
          </FlagButton>
        </FlagButtonWrapper>
        <StyledPhoneInput
          ref={(node) => {
            phoneInputRef.current = node
            if (ref) {
              if (typeof ref === 'function') {
                ref(node)
              } else {
                ref.current = node
              }
            }
          }}
          type="tel"
          value={displayValue}
          onChange={handlePhoneChange}
          onKeyDown={handleKeyDown}
          $size={size}
          $variant="default"
          $error={error}
          $hasLeftIcon={true}
          $hasRightIcon={false}
          disabled={disabled}
          placeholder={placeholder}
          {...props}
        />

        {isDropdownOpen && (
          <CountryDropdown
            ref={dropdownRef}
            $isOpen={isDropdownOpen}
            $fullWidth={fullWidth}
          >
            <SearchInputWrapper>
              <Input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setHighlightedIndex(0)
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                size="small"
                fullWidth
                leftIcon={<Icon name="search" size="small" />}
              />
            </SearchInputWrapper>

            <CountryList>
              {sortedCountries.length === 0 ? (
                <CountryItem $isSelected={false} $isHighlighted={false}>
                  <CountryName>{noMatchesText}</CountryName>
                </CountryItem>
              ) : (
                sortedCountries.map((country, index) => {
                  const isPreferred = preferredCountries.includes(country.code)
                  return (
                    <React.Fragment key={country.code}>
                      {isPreferred &&
                        index === 0 &&
                        preferredCountries.length > 0 && (
                          <CountryItem
                            $isSelected={false}
                            $isHighlighted={false}
                            style={{
                              fontWeight: 'bold',
                              fontSize: '12px',
                              opacity: 0.7,
                            }}
                          >
                            Preferred
                          </CountryItem>
                        )}
                      {!isPreferred &&
                        index > 0 &&
                        preferredCountries.includes(
                          sortedCountries[index - 1].code
                        ) && (
                          <CountryItem
                            $isSelected={false}
                            $isHighlighted={false}
                            style={{
                              fontWeight: 'bold',
                              fontSize: '12px',
                              opacity: 0.7,
                              marginTop: '8px',
                            }}
                          >
                            All Countries
                          </CountryItem>
                        )}
                      <CountryItem
                        onClick={() => handleCountrySelect(country)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        $isSelected={country.code === selectedCountry.code}
                        $isHighlighted={index === highlightedIndex}
                      >
                        <CountryFlag>{country.flag}</CountryFlag>
                        <CountryName>{country.name}</CountryName>
                        <CountryCode>{country.dialCode}</CountryCode>
                      </CountryItem>
                    </React.Fragment>
                  )
                })
              )}
            </CountryList>
          </CountryDropdown>
        )}
      </PhoneInputWrapper>
    )
  }
)

PhoneInput.displayName = 'PhoneInput'
